import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRouter } from "next13-progressbar";
import { CSSProperties } from "react";
import { useTranslations } from "next-intl";
import type { Merchant } from "@/dto/merchant";
import { shouldHardNavigate } from "@/utils/hardNavigation";

interface NavLinkProps {
  name: string;
  style?: CSSProperties;
  nameToUse?: string;
  showBottomBorder?: boolean;
  queryParams?: string;
  skipToID?: string;
  basePath?: string;
  hoverBgColor?: string;
  merchant?: Pick<
    Merchant,
    | "isRetail"
    | "isService"
    | "isRestaurant"
    | "isHybrid"
    | "isEssentialMerchant"
    | "hasOnlyRetailItems"
  >;
}

export const NavLink = ({
  name,
  style = {},
  nameToUse,
  showBottomBorder,
  queryParams,
  skipToID,
  basePath = "",
  hoverBgColor = "gray-50",
  merchant,
  ...props
}: NavLinkProps) => {
  const t = useTranslations("NavigationBar");
  const router = useRouter();
  const pathName = decodeURIComponent(usePathname() || "");
  const href = `${basePath}/${nameToUse ?? name}${queryParams ?? ""}`;
  const targetBase = `${basePath}/${nameToUse ?? name}`.toLowerCase();
  const shouldUseHardNavigation =
    merchant && shouldHardNavigate(href, merchant);
  const isActiveNavlink =
    pathName === targetBase ||
    pathName.startsWith(`${targetBase}/`) ||
    pathName === `${basePath}/${decodeURIComponent(name)}` ||
    showBottomBorder;

  const handleSkipBehavior = (e: React.SyntheticEvent) => {
    const mouseEvent = e as React.MouseEvent;
    if (
      mouseEvent.metaKey ||
      mouseEvent.ctrlKey ||
      mouseEvent.shiftKey ||
      mouseEvent.altKey
    ) {
      return false;
    }

    if (isActiveNavlink && skipToID) {
      e.preventDefault();
      const target = document.getElementById(skipToID);
      target?.focus();
      return true;
    }
    return false;
  };

  const handleClick = (e: React.MouseEvent) => {
    handleSkipBehavior(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === " ") {
      if (handleSkipBehavior(e)) return;

      e.preventDefault();

      if (shouldUseHardNavigation) {
        globalThis.location.assign(href);
        return;
      }

      router.push(href);
    }
  };

  const linkClassName = [
    "capitalize",
    "leading-6",
    "px-4",
    `hover:bg-${hoverBgColor}`,
    "text-center",
    "pb-3",
    "text-[16px]",
    "flex",
    "items-center",
    "sm:pt-2",
    `${isActiveNavlink ? "sm:pb-4" : "sm:pb-5"}`,
    "sm:px-4",
    `link ${isActiveNavlink ? "border-b-4" : ""}`,
  ].join(" ");
  const ariaLabel =
    isActiveNavlink && skipToID
      ? t("ariaActivePage", { currentPage: decodeURIComponent(name) })
      : `${decodeURIComponent(name)},`;

  if (shouldUseHardNavigation) {
    return (
      <a
        href={href}
        style={style}
        aria-current={isActiveNavlink ? "page" : undefined}
        aria-label={ariaLabel}
        {...props}
        className={linkClassName}
        onClick={handleSkipBehavior}
        onKeyDown={handleKeyDown}
      >
        {decodeURIComponent(name)}
      </a>
    );
  }

  return (
    <Link
      onClick={handleClick}
      href={href}
      onKeyDown={handleKeyDown}
      style={style}
      aria-current={isActiveNavlink ? "page" : undefined}
      aria-label={ariaLabel}
      {...props}
      className={linkClassName}
    >
      {decodeURIComponent(name)}
    </Link>
  );
};
